import { nanoid } from 'nanoid';
import { useQuery } from 'react-query';
import { type SceneCompareItem, SceneType } from '@types';
import { queryClient } from '@queryClient';
import { getLibMatrixCache, getLibPanoCache } from '@apis/mediaApis';

export function generateCompareItemId() {
  return `cp_${nanoid()}`;
}

export type CompareItemData =
  | {
      type: SceneType;
      data: {
        name: string;
        path: string;
        multires: string;
        cdnDomain: number;
        thumb: string;
        createDate: number;
      } | null;
    }
  | undefined;

export async function getCompareItemCache(
  data: SceneCompareItem | null
): Promise<CompareItemData> {
  if (!data) return;
  if (!data.materialId)
    return {
      type: data.type,
      data: null,
    };
  if (data.type === SceneType.pano) {
    const panoData = await getLibPanoCache(data.materialId);
    const { qiniuThumbUrl, ...rest } = panoData;
    return {
      type: SceneType.pano,
      data: { ...rest, thumb: qiniuThumbUrl },
    } as const;
  }
  if (data.type === SceneType.matrix) {
    const matrixData = await getLibMatrixCache(data.materialId);
    if (matrixData.length) {
      return {
        type: SceneType.matrix,
        data: matrixData[0],
      } as const;
    }
  }
  throw new Error('场景对比数据获取失败');
}

export function getCompareQueryKey(
  data: Pick<SceneCompareItem, 'type' | 'materialId'> | null
) {
  if (!data) return ['compare-item', 'null'];
  return ['compare-item', data.type, data.materialId];
}

export function useCompareItemQuery(data: SceneCompareItem | null) {
  return useQuery(getCompareQueryKey(data), () => getCompareItemCache(data), {
    staleTime: 30 * 60 * 1000, // 30min
    enabled: !!data,
  });
}

export function fetchCompareItemQuery(data: SceneCompareItem) {
  const queryKey = getCompareQueryKey(data);
  return queryClient.fetchQuery(queryKey);
}
